<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from "vue";
import { Validator } from "@zyzgroup/core-common";
import Text from "./Text.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Text },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    placeholderType?: "normal" | "underline" | "jump" | "jump-each";
    required?: boolean;

    showError?: boolean;
    validator?: Validator;
    defaultValue?: string;

    height?: string;
    type?: string;
    borderColor?: string;
    focusColor?: string;
    errorColor?: string;
    placeholderColor?: string;
    fontColor?: string;
    borderRadius?: string;
    duration?: number;
  }>(),
  {
    placeholder: "",
    placeholderType: "normal",
    required: false,
    showError: true,

    height: "44px",
    type: "text",
    borderColor: "#cccccc",
    focusColor: "#5a82f0",
    errorColor: "#ff5050",
    placeholderColor: "#8f8f8f",
    fontColor: "#333333",
    borderRadius: "4px",
    duration: 500
  }
);

const emits = defineEmits<{
  (event: "focus"): void;
  (event: "yz-focus"): void;
  (event: "blur"): void;
  (event: "yz-blur"): void;
  (event: "input", text: string): void;
  (event: "yz-input", text: string): void;
  (event: "error", text: string): void;
  (event: "yz-error", text: string): void;
}>();

const isTypeNormal = computed(() => props.placeholderType == "normal");
const isTypeUnderline = computed(() => props.placeholderType == "underline");
const isTypeJump = computed(() => props.placeholderType == "jump");
const isTypeJumpEach = computed(() => props.placeholderType == "jump-each");

const placeholders = props.placeholder.split("");
const placeholderJumpEachDuration = props.duration / placeholders.length;

const inputEl = ref();

const isFocus = ref(false);
const errorText = ref("");
const inputText = ref(props.defaultValue || "");

function focus() {
  isFocus.value = true;
  emits("focus");
  emits("yz-focus");
}

function blur() {
  isFocus.value = false;
  emits("blur");
  emits("yz-blur");
  validate();
}

function input(e: Event) {
  inputText.value = (e.target as HTMLInputElement).value;
  validate();
}

function validate() {
  if (inputText.value && props.validator) {
    const result = props.validator(inputText.value);
    if (!result.valid) {
      errorText.value = result.errors[0];
      emits("error", errorText.value);
      emits("yz-error", errorText.value);
      return;
    }
  }
  errorText.value = "";
  if (inputText.value) {
    emits("input", inputText.value);
    emits("yz-input", inputText.value);
  }
}

onMounted(() => {
  if (props.defaultValue) {
    inputEl.value.value = props.defaultValue;
  }
});

onUnmounted(() => {});

defineExpose({});
</script>

<template>
  <div class="input">
    <!-- error prompt top -->
    <div
      v-if="props.showError && errorText && !isTypeUnderline"
      class="error-prompt-top"
    >
      <Text
        type="warn"
        :color="props.errorColor"
      >
        {{ errorText }}
      </Text>
    </div>
    <!-- input wrap -->
    <div
      class="input-wrap"
      :class="isFocus || inputText ? 'focus' : ''"
    >
      <!-- placeholder -->
      <div
        class="placeholder"
        :style="{
          lineHeight: isFocus || inputText ? 1.3 : props.height,
          color: props.placeholderColor,
          transition: isTypeNormal ? '' : props.duration + 'ms',
          transform: isTypeNormal
            ? ''
            : isFocus || inputText
            ? `translateY(-100%)`
            : 'none'
        }"
      >
        <!-- placeholder normal -->
        <div
          v-if="!errorText && isTypeNormal && !isFocus && !inputText"
          :style="{
            paddingLeft: '0.5em'
          }"
        >
          {{ props.placeholder }}
        </div>
        <!-- placeholder jump -->
        <div v-if="!errorText && isTypeJump">
          {{ props.placeholder }}
        </div>
        <!-- placeholder jump-each -->
        <span
          v-if="!errorText && isTypeJumpEach"
          v-for="(item, index) in placeholders"
          :key="index"
          :style="{
            transition:
              props.duration + 'ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            transitionDelay: `${placeholderJumpEachDuration * index}ms`,
            transform:
              isFocus || inputText
                ? `translateX(${100 * index}%) translateY(-100%)`
                : `translateX(${100 * index}%)`
          }"
          class="single"
        >
          {{ item }}
        </span>
      </div>
      <input
        ref="inputEl"
        class="input-raw"
        :type="props.type"
        :required="props.required"
        autocomplete="off"
        :style="{
          height: props.height,
          lineHeight: props.height,
          color: props.fontColor,
          padding: isTypeUnderline ? '0.6em 0' : '0.6em',
          border: `${isTypeUnderline ? '0' : '1'}px solid transparent`,
          borderBottom: `1px solid transparent`,
          borderRadius: isTypeUnderline ? '0' : props.borderRadius,
          borderColor: errorText
            ? props.errorColor
            : isFocus
            ? props.focusColor
            : inputText || isTypeNormal || isTypeUnderline
            ? props.borderColor
            : 'transparent',
          transition: props.duration + 'ms'
        }"
        @focus="focus"
        @blur="blur"
        @input="input"
        @change="input"
      />
      <!-- background color and height -->
      <div
        class="background"
        v-if="!isTypeUnderline"
        :style="{
          transition: props.duration + 'ms',
          backgroundColor:
            isTypeNormal || isFocus || inputText
              ? 'transparent'
              : props.borderColor,
          height: isTypeNormal || isFocus || inputText ? props.height : '2px',
          borderRadius: props.borderRadius
        }"
      ></div>
    </div>
    <!-- error prompt bottom -->
    <div
      v-if="props.showError && errorText && isTypeUnderline"
      class="error-prompt-bottom"
    >
      <Text
        type="warn"
        :color="props.errorColor"
      >
        {{ errorText }}
      </Text>
    </div>
  </div>
</template>

<style scoped>
.input {
  position: relative;
  box-sizing: border-box;
  font-size: 1em;
}
/*
.error-prompt-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  transform: translateY(-100%);
}
*/
.error-prompt-top {
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  margin-bottom: 0.5em;
}
.error-prompt-bottom {
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  margin-top: 0.5em;
}
.input-wrap {
  position: relative;
  box-sizing: border-box;
}
.input-raw {
  display: block;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  background: transparent;
  outline: none;
  box-shadow: none;
  z-index: 10;
}
.background {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  pointer-events: none;
  z-index: 9;
}
.placeholder {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  pointer-events: none;
}
.focus .placeholder {
  font-size: 0.75em;
}
.placeholder > div {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.placeholder > span.single {
  position: absolute;
  left: 0;
  top: 0;
}
/*
input:-moz-placeholder,
textarea:-moz-placeholder,
input:-ms-input-placeholder,
textarea:-ms-input-placeholder,
input::-webkit-input-placeholder,
textarea::-webkit-input-placeholder {
  color: #C0C4CC;
}
*/
</style>
